/**
 * FaqPage — I-3. 자주묻는질문 (FAQ)
 *
 * - 검색 인풋: 엔터 시 클라이언트 필터
 * - 카테고리 탭: API 동적 로드 (전체 prepend)
 * - FAQ 아코디언: 단건 오픈, HTML 답변 렌더링
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Search } from '@indipia/ui-core';
import { fetchFaqCategories, fetchFaqs } from '../api/faqs';
import type { FaqItem } from '../api/faqs';

// ── 헬퍼 ────────────────────────────────────────────────────────────────────

function FaqRow({
  faq,
  isOpen,
  onToggle,
}: {
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <Button
        variant="ghost"
        onClick={onToggle}
        className="!h-auto !w-full !justify-between !gap-4 !rounded-none !px-5 !py-4 !transition-colors"
        style={{
          background: isOpen
            ? 'color-mix(in srgb, var(--gt-primary) 3%, var(--shell-surface))'
            : 'var(--shell-surface)',
        }}
      >
        <span
          className="flex-1 text-left text-sm font-semibold leading-snug"
          style={{ color: isOpen ? 'var(--gt-primary)' : 'var(--shell-on-surface)' }}
        >
          {faq.faqQuestion}
        </span>
        <svg
          className="shrink-0 transition-transform"
          style={{ opacity: 0.35, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: 'var(--shell-on-surface)' }}
          width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </Button>

      {isOpen && (
        <div
          className="border-b px-6 pb-5 pt-4"
          style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}
        >
          {faq.faqAnswer ? (
            <div
              className="faq-prose text-sm leading-relaxed"
              style={{ color: 'var(--shell-on-surface)' }}
              dangerouslySetInnerHTML={{ __html: faq.faqAnswer }}
            />
          ) : (
            <p className="text-sm text-text-muted">답변이 준비되지 않았습니다.</p>
          )}

          {faq.guideUrl && (
            <a
              href={faq.guideUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold"
              style={{ color: 'var(--gt-primary)' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              가이드 보기
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function FaqPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);

  const { data: categories = [] } = useQuery({
    queryKey: ['faq-categories'],
    queryFn: fetchFaqCategories,
  });

  const { data: faqs = [], isLoading } = useQuery({
    queryKey: ['faqs', selectedCategory],
    queryFn: () => fetchFaqs(selectedCategory, 'ZEU_FAQ_CATEGORY'),
  });

  function handleSearch(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      setSearchQuery(searchInput.trim());
      setOpenFaqId(null);
    }
  }

  function handleCategoryChange(code: string | null) {
    setSelectedCategory(code);
    setOpenFaqId(null);
    setSearchQuery('');
    setSearchInput('');
  }

  const filtered: FaqItem[] = searchQuery
    ? faqs.filter((f) => f.faqQuestion.toLowerCase().includes(searchQuery.toLowerCase()))
    : faqs;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl px-20 py-8 pb-16">

        {/* 헤더 */}
        <div className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--gt-primary)' }}>
            고객센터
          </p>
          <h1 className="mb-1 text-xl font-extrabold tracking-tight text-text">자주 묻는 질문</h1>
          <p className="text-xs text-text-muted">인디피아 스튜디오 이용 중 궁금한 점을 빠르게 찾아보세요</p>
        </div>

        {/* 검색 */}
        <div className="mb-5 flex">
          <Search
            value={searchInput}
            onChange={setSearchInput}
            onClear={() => setSearchQuery('')}
            onKeyDown={handleSearch}
            placeholder="궁금한 점을 검색해 보세요 (예: 결제, 에이전트, 비밀번호)"
          />
        </div>

        {/* 카테고리 탭 */}
        <div className="mb-0 flex gap-0.5 border-b" style={{ borderColor: 'var(--shell-border)' }}>
          {[{ code: null, name: '전체' }, ...categories.map((c) => ({ code: c.faqCategory, name: c.categoryName }))].map((tab) => (
            <Button
              key={tab.code ?? 'all'}
              variant="ghost"
              size="sm"
              onClick={() => handleCategoryChange(tab.code)}
              className="!rounded-none !px-4 !py-2 !text-xs !font-semibold !transition-colors hover:!bg-transparent"
              style={{
                color: selectedCategory === tab.code ? 'var(--gt-primary)' : 'var(--shell-on-surface-2)',
                borderBottom: selectedCategory === tab.code ? '2px solid var(--gt-primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {tab.name}
            </Button>
          ))}
        </div>

        {/* FAQ 목록 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin text-text-muted" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-text-muted">
              {searchQuery ? `"${searchQuery}"에 해당하는 FAQ가 없어요` : '해당 카테고리의 FAQ가 없어요'}
            </p>
          </div>
        ) : (
          <div
            className="mt-5 overflow-hidden rounded-xl border"
            style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}
          >
            {filtered.map((faq, i) => (
              <div key={faq.faqId} className={i > 0 ? 'border-t' : ''} style={{ borderColor: 'var(--shell-border)' }}>
                <FaqRow
                  faq={faq}
                  isOpen={openFaqId === faq.faqId}
                  onToggle={() => setOpenFaqId(openFaqId === faq.faqId ? null : faq.faqId)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
